import * as React from "react";
import styled from "styled-components";
import { select } from "@hubper/theme";
import mergeStyles from "../../util/mergeStyles";
import Icon from "../../elements/Icon";
import Text from "../../elements/Text";

import TimeBar from "./TimeBar";
import Spinner from "../../elements/Spinner";

import composeRefs from "../../util/composeRefs";

const Base = styled.div`
  width: 250px;
  height: 130px;
  border-radius: 5px;

  position: relative;
  overflow: hidden;
`;

const Background = styled.div<{ imageSrc: string }>`
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;

  background-color: ${select.color(["grey", 0])};
  background: url("${p => p.imageSrc}");
  background-size: cover;
  background-position: center center;
  filter: blur(8px) brightness(0.95);
`;

const Middle = styled.div`
  position: absolute;
  left: 16px;
  right: 16px;
  top: 35%;
  transform: translateY(-50%);
  display: flex;
`;

const LoaderWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MiddleLeft = styled.div<{ imageSrc: string }>`
  flex: 2 1;
  /* margin-right: 16px; */
  height: 50px;

  background-color: ${select.color(["grey", 0])};
  background: url("${p => p.imageSrc}");
  background-size: cover;
  background-position: center center;
  border-radius: 5px;
  box-shadow: 0px 0px 20px 0px rgba(0,0,0,0.3);
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 5 1;

  & > *:not(:last-child) {
    margin-right: 4px;
  }
`;

const Control = styled(Icon)`
  transition: 0.2s;
  opacity: 0.8;
  cursor: pointer;
  fill: white;

  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
`;

type PlaybackStatus = "play" | "pause";

type Props = {
  style?: React.CSSProperties;
  className?: string;
  imageUrl: string;
  audioUrl: string;
  loadingLabel: string;
  onPlaybackStatusChange?: (status: PlaybackStatus) => void;
  playbackStatus?: PlaybackStatus;
  audioRef?:
    | React.RefObject<HTMLAudioElement>
    | ((el: HTMLAudioElement) => void);
};

type State = {
  playbackStatus: PlaybackStatus;
  loading: boolean;
  duration: number | null;
  currentTime: number;
};

function PodcastPlayer(
  {
    style,
    className,
    imageUrl,
    audioUrl,
    loadingLabel,
    onPlaybackStatusChange,
    playbackStatus,
    ...props
  }: Props,
  ref: any
) {
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const [state, setState] = React.useState<State>({
    playbackStatus: "pause",
    loading: true,
    duration: null,
    currentTime: 0
  });

  React.useEffect(() => {
    if (!state.loading) {
      setState({ ...state, loading: true });
    }

    function onAutoLoad() {
      try {
        setState({
          ...state,
          duration: audioRef.current!.duration,
          loading: false
        });

        if (playbackStatus === "play") {
          audioRef.current!.play();
        }
      } catch (e) {
        console.error(e);
      }
    }

    audioRef.current!.addEventListener("loadeddata", onAutoLoad, true);
    audioRef.current!.setAttribute("src", audioUrl);
    audioRef.current!.load();

    return () =>
      audioRef.current!.removeEventListener("loadeddata", onAutoLoad);
  }, [audioUrl]);

  // React.useEffect(() => {
  //   if (
  //     (playbackStatus !== undefined ? playbackStatus : state.playbackStatus) ===
  //     "play"
  //   ) {
  //     audioRef.current!.play();
  //   } else {
  //     audioRef.current!.pause();
  //   }
  // }, [playbackStatus, state.playbackStatus]);

  function onPlay() {
    if (onPlaybackStatusChange) {
      onPlaybackStatusChange("play");
    } else {
      audioRef.current!.play();
      setState({ ...state, playbackStatus: "play" });
    }
  }

  function onPause() {
    if (onPlaybackStatusChange) {
      onPlaybackStatusChange("pause");
    } else {
      audioRef.current!.pause();
      setState({ ...state, playbackStatus: "pause" });
    }
  }

  function replay10() {
    const time = audioRef.current!.currentTime;
    audioRef.current!.currentTime = time - 10 < 0 ? 0 : time - 10;
  }

  function forward10() {
    const time = audioRef.current!.currentTime;
    audioRef.current!.currentTime =
      time + 10 > audioRef.current!.duration
        ? audioRef.current!.duration
        : time + 10;
  }

  return (
    <>
      <audio
        ref={composeRefs(audioRef, props.audioRef)}
        onTimeUpdate={() =>
          setState({ ...state, currentTime: audioRef.current!.currentTime })
        }
        hidden
      >
        Your browser does not support the audio element.
      </audio>
      <Base
        ref={ref}
        style={mergeStyles({ width: 250, height: 130 }, style)}
        className={className}
      >
        <Background imageSrc={imageUrl} />
        {state.loading ? (
          <LoaderWrapper>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
              }}
            >
              <Spinner size={24} c="white" />
              <Text
                fs={-1}
                style={{ display: "block", marginTop: 12 }}
                c="rgba(255,255,255, 0.9)"
              >
                {loadingLabel}
              </Text>
            </div>
          </LoaderWrapper>
        ) : (
          <Middle>
            <MiddleLeft imageSrc={imageUrl} />
            <Controls>
              <Control size={20} name="replay10" onClick={replay10} />
              {(playbackStatus !== undefined
                ? playbackStatus
                : state.playbackStatus) === "pause" ? (
                <Control size={40} name="video" onClick={onPlay} />
              ) : (
                <Control size={40} name="pause" onClick={onPause} />
              )}
              <Control size={20} name="forward10" onClick={forward10} />
            </Controls>
          </Middle>
        )}
        {!state.loading && (
          <TimeBar
            currentTime={state.currentTime}
            duration={state.duration || 1}
            onTimeChange={seconds => {
              audioRef.current!.currentTime = seconds;
            }}
          />
        )}
      </Base>
    </>
  );
}

export default React.forwardRef(PodcastPlayer);
